import { v4 as uuid } from 'uuid';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import {
	fireTrackAnalytics,
	type useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { getAiAttributes } from '../../common/get-ai-attributes.tsx';
import {
	AI_RESULT_ERROR_CATEGORIES,
	CLOSED_AI_MODAL_ABORT_REASON_MESSAGE,
	CLOSED_AI_MODAL_ABORT_REASON_NAME,
	JS_ERROR_ABORT_REASON_NAME,
} from '../../common/constants.tsx';
import { createConvoAiEndpoint, type JQLDebuggerType } from '../../services/convo-ai-client.tsx';
import { LruCache } from './lru-cache/index.tsx';
import type { OnClickArgs } from './types.tsx';

export const endpoint = createConvoAiEndpoint({
	url: '/gateway/api/assist/api/v1/plugin/execute',
	method: 'POST',
	experienceId: 'jira-nl-to-jql-errors',
	product: 'jira',
});

export const apiCache = LruCache<Promise<JQLDebuggerType['response']>>();

export const preloadApiCall = (
	query: string,
	jqlErrors?: string[],
	abortController = new AbortController(),
) => {
	const promise =
		apiCache.get(query)?.value ||
		endpoint(
			{
				name: 'jirajqldebug',
				schema: {
					type: 'jirajqldebug',
					userQuery: query,
					userQueryError: jqlErrors?.join('. \n') || '',
				},
			},
			{
				abortControllerSignal: abortController.signal,
			},
		);

	apiCache.set(query, promise);

	return promise;
};

export const preloadApiCallNew = (
	createAnalyticsEvent: ReturnType<typeof useAnalyticsEvents>['createAnalyticsEvent'],
	query: string,
	jqlErrors?: string[],
	abortController: AbortController = new AbortController(),
) => {
	const cachedApiCall = apiCache.get(query)?.value;

	if (fg('jql-fixer-cost-reduction-targeting-gate')) {
		if (!cachedApiCall) {
			fireTrackAnalytics(createAnalyticsEvent({}), 'jqlFixerApiCall initiated', {});
		}
	}

	const promise =
		cachedApiCall ||
		endpoint(
			{
				name: 'jirajqldebug',
				schema: {
					type: 'jirajqldebug',
					userQuery: query,
					userQueryError: jqlErrors?.join('. \n') || '',
				},
			},
			{
				abortControllerSignal: abortController.signal,
			},
		);

	apiCache.set(query, promise);

	return promise;
};

export async function onClick({
	testId,
	createAnalyticsEvent,
	query,
	debuggerStoreActions,
	jqlErrors,
	onSearch,
	isReadyForProactive = false,
}: OnClickArgs): Promise<void> {
	const singleInstrumentationID = uuid(); // TODO - remove with the fg cleanup of jql_fixer_analytics_fixes

	try {
		fireTrackAnalytics(
			createAnalyticsEvent({}),
			'aiInteraction initiated',
			getAiAttributes({
				singleInstrumentationID, // TODO - remove with the fg cleanup of jql_fixer_analytics_fixes
				isReadyForProactive,
			}),
		);

		const abortController = new AbortController();

		// When we trigger the JQL Debugger within NL-to-JQL, we want to exit the natural language mode
		// This is because two AI features cannot be nested within each other
		// Attempting to show two AI features at once also interferes with non functional aspects such as loading states, analytics and feedback collector
		debuggerStoreActions.exitNaturalLanguageMode();

		debuggerStoreActions.setStateOnFeatureInvoke({
			jqlQuery: query,
			singleInstrumentationID, // TODO - remove with the fg cleanup of jql_fixer_analytics_fixes
			errorSectionTestId: testId,
		});

		// Register callback to be called when there is a javascript error
		// Currently all it does is aborting any pending API calls
		debuggerStoreActions.setOnJsError(() => {
			abortController?.abort(
				new DOMException(CLOSED_AI_MODAL_ABORT_REASON_MESSAGE, JS_ERROR_ABORT_REASON_NAME),
			);
		});

		// Register callback to be used when the AI modal is dismissed
		debuggerStoreActions.setOnAiInteractionDismissed((dismissReason) => {
			// Abort any pending API calls
			abortController?.abort(
				new DOMException(CLOSED_AI_MODAL_ABORT_REASON_MESSAGE, CLOSED_AI_MODAL_ABORT_REASON_NAME),
			);

			// Fire the singleInstrumentation event
			fireTrackAnalytics(createAnalyticsEvent({}), 'aiInteraction dismissed', {
				...getAiAttributes({
					singleInstrumentationID, // TODO - remove with the fg cleanup of jql_fixer_analytics_fixes
					isReadyForProactive,
				}),
				dismissReason,
			});
		});

		/**
		 * Set the API response data on the store, so that the data can be consumed by the JQLDebuggerRenderSlot
		 *
		 * This is because the trigger and the render slot are used in two different packages
		 *  - Trigger is rendered inside the JQL Builder
		 *  - Render slot is in Issue Navigator
		 *
		 * By keeping these two components loosely coupled, we can style them independent of each other
		 * Yet they are highly cohesive, so that one can be controlled by the other by updating data on the store
		 *
		 * This approach also allows us to minimise the javascript bundles loaded for each component in their specific circumstances
		 * For example, the render-slot chunk is not loaded until the users hover over the trigger button
		 * Similarly the trigger-button chunk is not loaded unless there are errors in the jql-editor
		 */
		const response = fg('jql-fixer-cost-reduction-targeting-gate')
			? await preloadApiCallNew(createAnalyticsEvent, query, jqlErrors, abortController)
			: await preloadApiCall(query, jqlErrors, abortController);
		const syntaxCorrectionAttemptErrors = response?.metadata?.value?.errors || [];

		if (syntaxCorrectionAttemptErrors.length) {
			fireTrackAnalytics(createAnalyticsEvent({}), 'aiResult error', {
				...getAiAttributes({
					singleInstrumentationID, // TODO - remove with the fg cleanup of jql_fixer_analytics_fixes
					isReadyForProactive,
				}),
				aiResultErrorCategory: AI_RESULT_ERROR_CATEGORIES.FIXED_JQL_HAS_ERRORS,
			});
		}

		debuggerStoreActions.setJqlErorCorrectionData({
			userQueryError: jqlErrors || [],
			onSearch: onSearch || (() => undefined),
			jqlDiffTokens: response?.metadata?.value?.diff || [],
			syntaxCorrectionAttemptErrors,
		});
	} catch (e) {
		apiCache.delete(query);

		const isNetworkCallAbort = e instanceof DOMException;

		fireErrorAnalytics({
			event: createAnalyticsEvent({}),
			meta: {
				id: 'JQL_Debugger',
				packageName: '@atlassian/jira-jql-debugger',
				teamName: 'gravity-ai',
			},
			attributes: {
				...getAiAttributes({
					singleInstrumentationID, // TODO - remove with the fg cleanup of jql_fixer_analytics_fixes
					isReadyForProactive,
				}),
				isNetworkCallAbort,
			},
			error: e instanceof Error ? e : undefined,
			// When the network call is aborted due to dismissing the modal, then we don't want to send the error to Sentry as
			// it is not necessarily an error (e.g, a user could simply be searching for a different query)
			skipSentry: isNetworkCallAbort && e.name === CLOSED_AI_MODAL_ABORT_REASON_NAME,
		});

		if (isNetworkCallAbort) {
			// When network call is aborted due to a javascript error, then don't change the feature state
			if (e.name === JS_ERROR_ABORT_REASON_NAME) {
				return;
			}
			// When network call is aborted due to dismissing the modal, then we reset the feature state to initial state
			if (e.name === CLOSED_AI_MODAL_ABORT_REASON_NAME) {
				debuggerStoreActions.resetStatus('NETWORK_CALL_ABORT');
				return;
			}
		}

		// Do not fire the unified ai anlytics event for aiResult error when the API call is aborted
		// i.e. the interaction was dismissed, but not necessarily resulted in an error
		fireTrackAnalytics(createAnalyticsEvent({}), 'aiResult error', {
			...getAiAttributes({
				singleInstrumentationID, // TODO - remove with the fg cleanup of jql_fixer_analytics_fixes
				isReadyForProactive,
			}),
			aiResultErrorCategory: AI_RESULT_ERROR_CATEGORIES.SERVER_ERROR,
		});

		// Explicitly update the status to be RESULT_ERROR in the fallthrough case
		debuggerStoreActions.setStatus('RESULT_ERROR');
	}
}
